import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, ShieldCheck, Mail } from 'lucide-react';
import { Input, Button, Checkbox } from '@heroui/react';
import { useAuth } from '@/contexts/AuthContext';
import { LoginRequest } from '@saas-platform/shared-types';
import { getErrorMessage } from '@/utils/errorHandler';
import { FullScreenSignupLayout } from '@/components/ui/full-screen-signup';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/useToast';

const flatInputClassNames = {
  inputWrapper: 'bg-default-100 hover:bg-default-200/70 focus-within:!bg-default-100 rounded-xl shadow-none',
  label: 'text-sm font-semibold text-gray-800',
};

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [resendingCode, setResendingCode] = useState(false);

  // Two-Factor Authentication step state
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);

  const { login, verifyTwoFactor, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData);
      if (result && result.twoFactorRequired) {
        setNeedsTwoFactor(true);
        setTwoFactorCode('');
        setUseBackupCode(false);
      }
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      if (
        errorMessage.toLowerCase().includes('not verified') ||
        errorMessage.toLowerCase().includes('verification')
      ) {
        setVerifyEmail(formData.email);
        setNeedsVerification(true);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async () => {
    const trimmed = twoFactorCode.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      await verifyTwoFactor({
        code: trimmed,
        isBackupCode: useBackupCode,
        trustDevice,
      });
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyCode || verifyCode.length !== 6) return;
    setLoading(true);
    try {
      await apiService.client.post('/api/auth/verify', { email: verifyEmail, code: verifyCode });
      // Verification succeeded — auto-login
      const result = await login({ email: verifyEmail, password: formData.password });
      if (result && result.twoFactorRequired) {
        setNeedsVerification(false);
        setNeedsTwoFactor(true);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendingCode(true);
    try {
      await apiService.client.post('/api/auth/resend-verification', { email: verifyEmail });
      toast.success('Verification code resent.');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to resend code.');
    } finally {
      setResendingCode(false);
    }
  };

  const rightTitle = needsTwoFactor
    ? 'Two-Factor Authentication'
    : needsVerification
    ? 'Verify Account'
    : 'Welcome back';

  const rightSubtitle = needsTwoFactor
    ? useBackupCode
      ? 'Enter one of your single-use backup recovery codes'
      : 'Enter the 6-digit code from your authenticator app'
    : needsVerification
    ? `Enter the 6-digit code sent to ${verifyEmail}`
    : 'Sign in to your account';

  const rightIcon = needsTwoFactor ? (
    <ShieldCheck className="w-5 h-5 text-primary" />
  ) : needsVerification ? (
    <Mail className="w-5 h-5 text-primary" />
  ) : (
    <LogIn className="w-5 h-5 text-primary" />
  );

  return (
    <FullScreenSignupLayout
      leftHeadline=""
      rightTitle={rightTitle}
      rightSubtitle={rightSubtitle}
      icon={rightIcon}
      maxWidth="max-w-xl"
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          if (needsTwoFactor) {
            e.preventDefault();
            void handleTwoFactorSubmit();
          } else {
            void handleSubmit(e);
          }
        }}
      >
        {needsTwoFactor ? (
          <>
            <Input
              label={useBackupCode ? 'Backup recovery code' : '6-digit authenticator code'}
              labelPlacement="outside"
              type="text"
              inputMode={useBackupCode ? 'text' : 'numeric'}
              maxLength={useBackupCode ? 16 : 6}
              placeholder={useBackupCode ? 'e.g. a1b2c-d3e4f' : '000000'}
              value={twoFactorCode}
              onChange={(e) =>
                setTwoFactorCode(
                  useBackupCode
                    ? e.target.value.trim()
                    : e.target.value.replace(/\D/g, '').slice(0, 6)
                )
              }
              autoFocus
              variant="flat"
              classNames={{
                inputWrapper:
                  'bg-default-100 hover:bg-default-200/70 focus-within:!bg-default-100 rounded-xl h-12 shadow-none',
                input: 'text-center text-lg font-medium font-mono text-gray-900',
                label: 'text-sm font-medium text-gray-800',
              }}
            />

            <Checkbox
              size="sm"
              isSelected={trustDevice}
              onValueChange={setTrustDevice}
              classNames={{ label: 'text-xs text-gray-600' }}
            >
              Trust this device for 30 days
            </Checkbox>

            <Button
              type="submit"
              color="primary"
              className="w-full font-medium rounded-full bg-[#172e50] text-white shadow-none h-11"
              isLoading={loading}
              isDisabled={!loading && (useBackupCode ? twoFactorCode.length < 6 : twoFactorCode.length !== 6)}
            >
              Verify & Sign In
            </Button>

            <div className="flex items-center justify-between text-xs">
              <Button
                type="button"
                variant="light"
                size="sm"
                onPress={() => {
                  setNeedsTwoFactor(false);
                  setTwoFactorCode('');
                }}
                className="font-medium text-[#172e50] px-2 h-7"
              >
                Back to login
              </Button>
              <Button
                type="button"
                variant="light"
                size="sm"
                onPress={() => {
                  setUseBackupCode(!useBackupCode);
                  setTwoFactorCode('');
                }}
                className="font-medium text-gray-600 hover:text-gray-900 px-2 h-7 underline underline-offset-2"
              >
                {useBackupCode ? 'Use authenticator code' : 'Use a backup code'}
              </Button>
            </div>
          </>
        ) : needsVerification ? (
          <>
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-700">
              <p>
                We sent a 6-digit confirmation code to{' '}
                <span className="font-semibold text-gray-900">{verifyEmail}</span>. Enter it below to activate your account.
              </p>
            </div>

            <Input
              label="6-digit verification code"
              labelPlacement="outside"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              variant="flat"
              classNames={{
                inputWrapper:
                  'bg-default-100 hover:bg-default-200/70 focus-within:!bg-default-100 rounded-xl h-14 shadow-none',
                input: 'text-center text-2xl font-semibold font-mono text-gray-900',
                label: 'text-sm font-semibold text-gray-800',
              }}
            />

            <Button
              type="button"
              color="primary"
              className="w-full font-semibold rounded-xl bg-[#172e50] text-white shadow-none h-11"
              isLoading={loading}
              isDisabled={!loading && verifyCode.length !== 6}
              onPress={handleVerify}
            >
              Verify & Sign In
            </Button>

            <div className="flex items-center justify-between text-xs">
              <Button
                type="button"
                variant="light"
                size="sm"
                onPress={() => setNeedsVerification(false)}
                className="font-semibold text-[#172e50] px-2 h-7"
              >
                Back to login
              </Button>
              <Button
                type="button"
                variant="light"
                size="sm"
                onPress={handleResendCode}
                isLoading={resendingCode}
                className="font-semibold text-gray-500 hover:text-gray-700 px-2 h-7 underline underline-offset-2"
              >
                Resend code
              </Button>
            </div>
          </>
        ) : (
          <>
            <Input
              label="Email address"
              name="email"
              type="email"
              autoComplete="email"
              isRequired
              labelPlacement="outside-top"
              value={formData.email}
              onChange={handleChange}
              variant="flat"
              classNames={flatInputClassNames}
            />

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-800">
                  Password <span className="text-danger">*</span>
                </span>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-[#172e50] hover:underline underline-offset-2"
                >
                  Forgot password?
                </Link>
              </div>

              <Input
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                isRequired
                aria-label="Password"
                value={formData.password}
                onChange={handleChange}
                variant="flat"
                endContent={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                }
                classNames={flatInputClassNames}
              />
            </div>

            <Button
              type="submit"
              color="primary"
              className="w-full font-semibold rounded-full bg-[#172e50] text-white shadow-none h-11 mt-2"
              isLoading={loading}
            >
              Sign In
            </Button>
          </>
        )}
      </form>

      <div className="mt-6 text-center text-sm text-gray-600">
        Don&apos;t have an account?{' '}
        <Link to="/register" className="font-semibold text-[#172e50] hover:underline underline-offset-2">
          Start 14-day free trial
        </Link>
      </div>

      <p className="mt-8 text-center text-xs text-gray-400">
        By signing in, you agree to our{' '}
        <Link to="/terms" className="font-semibold text-gray-600 hover:underline">
          Terms
        </Link>{' '}
        and{' '}
        <Link to="/privacy" className="font-semibold text-gray-600 hover:underline">
          Privacy Policy
        </Link>
        .
      </p>
    </FullScreenSignupLayout>
  );
};

export default LoginPage;
